import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatSimpleMoney } from "@repo/components/src/utils/money";
import { formatNumber } from "@repo/components/src/utils/numbers";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { ArrowDown, ArrowUp, Star } from "lucide-react-native";
import React, { useMemo, useState } from "react";

dayjs.extend(duration);

type WorkOrder = CampaignAnalyticFragmentFragment["workOrders"][number];

type SortKey = "evaluation" | "clips" | "speed" | "revisions" | "cost";

type SortDir = "asc" | "desc";

type Props = {
  workOrders: WorkOrder[];
};

const DEFAULT_DIR: Record<SortKey, SortDir> = {
  evaluation: "desc",
  clips: "desc",
  speed: "asc",
  revisions: "asc",
  cost: "asc",
};

const Ranking = React.memo(({ workOrders }: Props) => {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "evaluation",
    dir: "desc",
  });

  const enriched = useMemo(() => {
    return workOrders.map((wo) => {
      const speed =
        wo.timeToDeliver !== null && wo.timeToDeliver !== undefined
          ? wo.timeToDeliver / 86_400
          : null;
      const clips = wo.details.length;
      const totalRevisions = wo.details.reduce(
        (acc, d) => acc + (d.revisions ?? 0),
        0,
      );
      const avgRevisions = clips ? totalRevisions / clips : 0;
      const investmentMoney = wo.investment;
      const investmentAmount = Number(investmentMoney?.amount ?? 0);
      return {
        wo,
        evaluation: wo.evaluation ?? null,
        clips,
        speed,
        avgRevisions,
        costPerClip: clips ? investmentAmount / clips : 0,
        costPerClipCurrency: investmentMoney?.currency.code ?? null,
      };
    });
  }, [workOrders]);

  const sorted = useMemo(() => {
    const copy = [...enriched];
    const dir = sort.dir === "desc" ? -1 : 1;
    const nullLast = (n: number | null) =>
      n === null ? Number.POSITIVE_INFINITY * dir * -1 : n;
    copy.sort((a, b) => {
      if (sort.key === "evaluation") {
        return (nullLast(a.evaluation) - nullLast(b.evaluation)) * dir;
      }
      if (sort.key === "clips") return (a.clips - b.clips) * dir;
      if (sort.key === "speed") {
        return (nullLast(a.speed) - nullLast(b.speed)) * dir;
      }
      if (sort.key === "revisions") {
        return (a.avgRevisions - b.avgRevisions) * dir;
      }
      return (a.costPerClip - b.costPerClip) * dir;
    });
    return copy;
  }, [enriched, sort]);

  const handleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: DEFAULT_DIR[key] },
    );
  };

  if (!workOrders.length) {
    return (
      <Text size="sm" className="text-typography-500">
        Aún no hay creadores con clips entregados.
      </Text>
    );
  }

  return (
    <VStack space="sm">
      <Box className="border border-outline-100 rounded-2xl overflow-x-auto">
        <Box className="min-w-[900px]">
          <HStack className="bg-background-50 border-b border-outline-100 px-3 py-2.5">
            <HeaderCell className="w-[40px]">#</HeaderCell>
            <HeaderCell className="flex-1 min-w-[220px]" align="start">
              Creador
            </HeaderCell>
            <SortableHeader
              className="w-[110px]"
              align="end"
              label="Evaluación"
              sortKey="evaluation"
              currentSort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              className="w-[100px]"
              align="end"
              label="N° de clips"
              sortKey="clips"
              currentSort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              className="w-[140px]"
              align="end"
              label="Tiempo a video"
              sortKey="speed"
              currentSort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              className="w-[120px]"
              align="end"
              label="Revisiones"
              sortKey="revisions"
              currentSort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              className="w-[160px]"
              align="end"
              label="Costo / clip"
              sortKey="cost"
              currentSort={sort}
              onSort={handleSort}
            />
          </HStack>
          {sorted.map((row, index) => {
            const avatar = row.wo.avatar;
            return (
              <HStack
                key={row.wo.id}
                className="px-3 py-3 border-b border-outline-100 items-center"
              >
                <Cell className="w-[40px]">
                  <Text size="sm" className="text-typography-500 font-bold">
                    {index + 1}
                  </Text>
                </Cell>
                <Cell className="flex-1 min-w-[220px]">
                  <HStack space="sm" className="items-center">
                    <Avatar size="sm">
                      <AvatarFallbackText>{row.wo.fullName}</AvatarFallbackText>
                      <AvatarImage source={{ uri: avatar?.url }} />
                    </Avatar>
                    <Text
                      size="sm"
                      className="text-typography-900 font-semibold"
                      numberOfLines={1}
                    >
                      {row.wo.fullName}
                    </Text>
                  </HStack>
                </Cell>
                <Cell className="w-[110px]" align="end">
                  {row.evaluation !== null ? (
                    <Stars value={row.evaluation} />
                  ) : (
                    <Text
                      size="sm"
                      className="text-typography-300 font-semibold"
                    >
                      —
                    </Text>
                  )}
                </Cell>
                <Cell className="w-[100px]" align="end">
                  <Text size="sm" className="text-typography-900 font-semibold">
                    {row.clips}
                  </Text>
                </Cell>
                <Cell className="w-[140px]" align="end">
                  <Text size="sm" className="text-typography-900 font-semibold">
                    {row.speed !== null ? `${row.speed.toFixed(1)} días` : "—"}
                  </Text>
                </Cell>
                <Cell className="w-[120px]" align="end">
                  <Text size="sm" className="text-typography-900 font-semibold">
                    {row.avgRevisions.toFixed(1)}
                  </Text>
                </Cell>
                <Cell className="w-[160px]" align="end">
                  <Text size="sm" className="text-typography-900 font-semibold">
                    {row.clips && row.costPerClipCurrency
                      ? formatSimpleMoney({
                          amount: String(row.costPerClip),
                          currency: row.costPerClipCurrency,
                        })
                      : "—"}
                  </Text>
                </Cell>
              </HStack>
            );
          })}
        </Box>
      </Box>
      <Text size="2xs" className="text-typography-400">
        {formatNumber(workOrders.length)} creadores
      </Text>
    </VStack>
  );
});

Ranking.displayName = "UgcRanking";

const HeaderCell = ({
  className,
  align = "start",
  children,
}: {
  className: string;
  align?: "start" | "end";
  children: React.ReactNode;
}) => (
  <Box className={`${className} ${align === "end" ? "items-end" : ""}`}>
    <Text size="2xs" className="text-typography-500 uppercase font-semibold">
      {children}
    </Text>
  </Box>
);

const SortableHeader = ({
  className,
  align = "start",
  label,
  sortKey,
  currentSort,
  onSort,
}: {
  className: string;
  align?: "start" | "end";
  label: string;
  sortKey: SortKey;
  currentSort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}) => {
  const isActive = currentSort.key === sortKey;
  return (
    <Pressable className={className} onPress={() => onSort(sortKey)}>
      <HStack
        space="xs"
        className={`items-center ${align === "end" ? "justify-end" : ""}`}
      >
        <Text
          size="2xs"
          className={
            isActive
              ? "text-brand-500 uppercase font-bold"
              : "text-typography-500 uppercase font-semibold"
          }
        >
          {label}
        </Text>
        {isActive ? (
          <Icon
            as={currentSort.dir === "asc" ? ArrowUp : ArrowDown}
            size="2xs"
            className="text-brand-500"
          />
        ) : null}
      </HStack>
    </Pressable>
  );
};

const Cell = ({
  className,
  align,
  children,
}: {
  className: string;
  align?: "end";
  children: React.ReactNode;
}) => (
  <Box className={`${className} ${align === "end" ? "items-end" : ""}`}>
    {children}
  </Box>
);

const Stars = ({ value }: { value: number }) => {
  const filled = Math.round(Math.max(0, Math.min(5, value)));
  return (
    <HStack space="xs" className="items-center">
      {Array.from({ length: 5 }).map((_, i) => {
        const isFilled = i < filled;
        return (
          <Icon
            key={i}
            as={Star}
            size="xs"
            className={
              isFilled ? "fill-brand-500 text-brand-500" : "text-typography-200"
            }
          />
        );
      })}
    </HStack>
  );
};

export default Ranking;
